import { useEffect, useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { commonsConfig } from '../config'
import { apiSend } from '../lib/api'
import { useLogout } from '../lib/session'
import { getSessionExpiry, subscribeSessionExpiry } from '../lib/sessionClock'
import { Button } from './ui'

const WARNING_MS = 30_000

// Seul un geste de l'utilisateur prolonge la session : un appel automatique
// garderait ouverte indefiniment une page laissee ouverte.
export default function SessionTimeoutWarning() {
  const navigate = useNavigate()
  const logout = useLogout()
  const extend = useMutation({ mutationFn: () => apiSend<null>('/auth/session/extend', 'POST') })

  const [expiresAt, setExpiresAt] = useState(getSessionExpiry)
  const [now, setNow] = useState(() => Date.now())
  const expired = useRef(false)

  useEffect(() => subscribeSessionExpiry(setExpiresAt), [])

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [])

  const remaining = expiresAt === null ? null : expiresAt - now

  useEffect(() => {
    if (remaining === null || remaining > 0 || expired.current) {
      return
    }

    // Echeance atteinte, ou session fermee depuis un autre onglet (0).
    expired.current = true
    const leave = () => void navigate({ to: '/login', search: { expired: true } })
    if (expiresAt === 0) {
      leave()
    } else {
      logout.mutate(undefined, { onSettled: leave })
    }
  }, [remaining, expiresAt, logout, navigate])

  if (remaining === null || remaining > WARNING_MS || remaining <= 0) {
    return null
  }

  const seconds = Math.ceil(remaining / 1000)

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="session-warning-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-950/40 p-4"
    >
      <div className="w-full max-w-sm rounded-2xl border border-neutral-200 bg-white p-6 shadow-xl dark:border-neutral-800 dark:bg-neutral-900">
        <h2
          id="session-warning-title"
          className="text-lg font-semibold text-neutral-900 dark:text-neutral-50"
        >
          Vous allez etre deconnecte
        </h2>
        <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">
          Sans activite depuis bientot {commonsConfig().sessionMinutes} minutes, votre session se
          fermera dans{' '}
          <span className="font-semibold tabular-nums">{seconds} s</span>.
        </p>
        <div className="mt-6 flex gap-2">
          <Button
            type="button"
            size="sm"
            autoFocus
            disabled={extend.isPending}
            onClick={() => extend.mutate()}
          >
            Rester connecte
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => {
              expired.current = true
              logout.mutate(undefined, { onSettled: () => void navigate({ to: '/login' }) })
            }}
          >
            Se deconnecter
          </Button>
        </div>
      </div>
    </div>
  )
}
